import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import {
  formatSubmissionFieldValue,
  getOrderedSubmissionDataEntries,
  getSubmissionEntryHeading,
} from '../../utils/formSubmissionDisplay';
import PotentialSpamChip, { isPotentialSpam } from './PotentialSpamChip';

/**
 * Same "Entry details" popup as Form submissions — full field list in definition order.
 */
function FormSubmissionEntryDialog({
  open,
  onClose,
  entry,
  form,
  footer = null,
}) {
  const formName = form?.name || entry?.formName || '';
  const createdAt = entry?.createdAt;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      aria-labelledby="form-submission-entry-dialog-title"
    >
      <DialogTitle
        id="form-submission-entry-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          pr: 1,
        }}
      >
        <Box>
          <Typography component="span" variant="h6" fontWeight={600}>
            Entry details
          </Typography>
          {entry && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {formName ? `${formName} · ` : ''}
              {createdAt ? new Date(createdAt).toLocaleString('en-GB') : ''}
            </Typography>
          )}
          {isPotentialSpam(entry) && (
            <Box sx={{ mt: 1 }}>
              <PotentialSpamChip />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Our spam checks flagged this enquiry. It may still be genuine.
              </Typography>
            </Box>
          )}
        </Box>
        <IconButton
          aria-label="Close"
          size="small"
          onClick={onClose}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {entry?.data && Object.keys(entry.data).length > 0 ? (
          getOrderedSubmissionDataEntries(entry.data, form?.fields).map(([key, value]) => {
            const fieldDef = Array.isArray(form?.fields)
              ? form.fields.find((f) => f && f.name === key)
              : null;
            const heading = getSubmissionEntryHeading(key, fieldDef);
            const displayValue = formatSubmissionFieldValue(
              key,
              value,
              form?.fields
            );
            return (
              <Box
                key={key}
                sx={{
                  mb: 2,
                  pb: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-of-type': { borderBottom: 'none', pb: 0, mb: 0 },
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                  {heading}
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {displayValue}
                </Typography>
              </Box>
            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary">
            No field data on this submission.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        {footer}
        <Button variant="text" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FormSubmissionEntryDialog;
